import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { driverLogin, getDriverToken } from "@/features/driver/api/driverApi";
import { DriverLanguageProvider, useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";
import LanguageSwitcher from "@/features/driver/components/LanguageSwitcher";

export default function DriverLogin() {
  return (
    <DriverLanguageProvider>
      <DriverLoginContent />
    </DriverLanguageProvider>
  );
}

function DriverLoginContent() {
  const navigate = useNavigate();
  const { language, setLanguage, t, availableLanguages } = useDriverLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (getDriverToken()) {
      navigate("/driver/dashboard", { replace: true });
    }
  }, [navigate]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await driverLogin(email, password);
      navigate("/driver/dashboard");
    } catch (err: any) {
      setError(err?.message ?? t("defaultLoginError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex min-h-[100dvh] flex-col justify-center overflow-x-hidden bg-europcarGreen px-4 py-10 text-white [padding-top:max(2.5rem,env(safe-area-inset-top))] [padding-bottom:max(2.5rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto w-full max-w-md">
        <img src="/logo.svg" alt="Europcar" className="mb-8 h-20 w-auto rounded-md" />
        <h1 className="text-2xl font-semibold tracking-tight">{t("driverPortalTitle")}</h1>
        <p className="mt-2 text-sm font-medium text-white/90">{t("driverPortalSubtitle")}</p>

        <form onSubmit={submit} className="mt-8 space-y-4 rounded-xl bg-white p-5 text-neutral-950 shadow-xl shadow-black/20">
          <label className="block text-sm font-medium" htmlFor="driver-email">
            {t("emailLabel")}
            <input
              id="driver-email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 h-12 w-full rounded-md border border-neutral-300 px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
              type="email"
              autoComplete="username"
              required
            />
          </label>
          <label className="block text-sm font-medium" htmlFor="driver-password">
            {t("passwordLabel")}
            <input
              id="driver-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 h-12 w-full rounded-md border border-neutral-300 px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          {error && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
          <Button className="h-12 w-full text-base" type="submit" disabled={isLoading}>
            {isLoading ? t("signingIn") : t("signInButton")}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs font-medium text-white/80">{t("authorizedOnly")}</p>

        <LanguageSwitcher value={language} onChange={setLanguage} languages={availableLanguages} className="mt-4 justify-center" />
      </div>
    </main>
  );
}
